/**
 * Realistic seeded sample data for the Investor Dashboard previews.
 *
 * Goal: help visitors picture exactly what they receive at each
 * subscription tier before they pay. Numbers are illustrative but
 * tuned to feel like a real investor's portfolio — not lorem ipsum.
 *
 * Keep this file free of external imports so previews stay snappy
 * and component-render cheap. Charts are drawn from these arrays
 * via tiny inline SVG, never a chart library.
 */

const LITE_PROPERTIES = [
    {
        id: "p-oak-123",
        address: "123 Oak Street",
        city: "Austin, TX 78702",
        risk_score: 62,
        risk_band: "Elevated",
        repair_exposure_low: 18500,
        repair_exposure_high: 27000,
        high_priority_issues: 3,
        health_score: 71,
        report_type: "Standard",
        analyzed_at: "2 days ago",
        top_issue: "Aging HVAC system + minor foundation settlement",
    },
    {
        id: "p-maple-456",
        address: "456 Maple Avenue",
        city: "Austin, TX 78704",
        risk_score: 41,
        risk_band: "Moderate",
        repair_exposure_low: 7800,
        repair_exposure_high: 12500,
        high_priority_issues: 1,
        health_score: 84,
        report_type: "Standard",
        analyzed_at: "5 days ago",
        top_issue: "Roof reaching end-of-life — 3-5 yrs remaining",
    },
    {
        id: "p-cedar-789",
        address: "789 Cedar Lane",
        city: "Round Rock, TX 78664",
        risk_score: 28,
        risk_band: "Low",
        repair_exposure_low: 3200,
        repair_exposure_high: 6100,
        high_priority_issues: 0,
        health_score: 92,
        report_type: "Standard",
        analyzed_at: "1 week ago",
        top_issue: "Cosmetic items only — clean inspection",
    },
];

const PRO_EXTRA = [
    { id: "p-elm-201", address: "201 Elm Court", city: "Austin, TX 78745", risk_score: 74, risk_band: "Elevated", repair_exposure_low: 28000, repair_exposure_high: 41000, high_priority_issues: 4, health_score: 64, report_type: "Premium", analyzed_at: "1 day ago", leverage: "strong", top_issue: "Foundation movement + electrical panel updates" },
    { id: "p-pine-318", address: "318 Pine Ridge Dr", city: "Cedar Park, TX 78613", risk_score: 53, risk_band: "Moderate", repair_exposure_low: 14500, repair_exposure_high: 21000, high_priority_issues: 2, health_score: 76, report_type: "Premium", analyzed_at: "3 days ago", leverage: "moderate", top_issue: "Plumbing supply line corrosion" },
    { id: "p-birch-512", address: "512 Birch Hollow", city: "Pflugerville, TX 78660", risk_score: 38, risk_band: "Moderate", repair_exposure_low: 6500, repair_exposure_high: 11200, high_priority_issues: 1, health_score: 81, report_type: "Premium", analyzed_at: "4 days ago", leverage: "moderate", top_issue: "Water heater + minor drainage grading" },
    { id: "p-willow-44", address: "44 Willow Bend", city: "Leander, TX 78641", risk_score: 67, risk_band: "Elevated", repair_exposure_low: 22000, repair_exposure_high: 33000, high_priority_issues: 3, health_score: 68, report_type: "Premium", analyzed_at: "6 days ago", leverage: "strong", top_issue: "Roof + chimney flashing + 2 financing flags" },
    { id: "p-aspen-91", address: "91 Aspen Crossing", city: "Hutto, TX 78634", risk_score: 22, risk_band: "Low", repair_exposure_low: 2400, repair_exposure_high: 4800, high_priority_issues: 0, health_score: 94, report_type: "Premium", analyzed_at: "8 days ago", leverage: "weak", top_issue: "Minor cosmetic — turnkey candidate" },
    { id: "p-magn-7", address: "7 Magnolia Park", city: "Buda, TX 78610", risk_score: 58, risk_band: "Moderate", repair_exposure_low: 16800, repair_exposure_high: 24500, high_priority_issues: 2, health_score: 72, report_type: "Premium", analyzed_at: "9 days ago", leverage: "moderate", top_issue: "Sewer scope flag + HVAC capacity" },
    { id: "p-sage-66", address: "66 Sage Meadow", city: "Kyle, TX 78640", risk_score: 81, risk_band: "High", repair_exposure_low: 34000, repair_exposure_high: 48000, high_priority_issues: 5, health_score: 56, report_type: "Premium", analyzed_at: "10 days ago", leverage: "strong", top_issue: "Active water intrusion + structural follow-up" },
];

// Lite items get default "weak/moderate" leverage so Pro/Plus tables stay rich.
const LITE_WITH_LEVERAGE = LITE_PROPERTIES.map((p, i) => ({
    ...p,
    leverage: ["moderate", "moderate", "weak"][i],
}));

const PRO_PROPERTIES = [...LITE_WITH_LEVERAGE, ...PRO_EXTRA];

// Plus tier — 25+ properties. We synthesize realistic-looking
// continuation rows so the volume feels operational.
const PLUS_EXTRA = [
    ["1402 Riverbend", "San Marcos, TX", 49, 13500, "moderate", "Standard"],
    ["88 Holly Hill", "Georgetown, TX", 63, 19800, "strong", "Premium"],
    ["230 Cypress Trail", "Manor, TX", 35, 7100, "weak", "Standard"],
    ["57 Juniper Way", "Bastrop, TX", 71, 26500, "strong", "Premium"],
    ["904 Sycamore", "Taylor, TX", 26, 4400, "weak", "Standard"],
    ["18 Redwood Pl", "Lago Vista, TX", 55, 15200, "moderate", "Premium"],
    ["301 Hawthorn Ln", "Dripping Springs, TX", 44, 9700, "moderate", "Premium"],
    ["77 Beechwood Ct", "Liberty Hill, TX", 78, 31200, "strong", "Premium"],
    ["1208 Spruce Ridge", "Wimberley, TX", 32, 5900, "weak", "Standard"],
    ["19 Hickory Bend", "Elgin, TX", 60, 17600, "moderate", "Premium"],
    ["455 Dogwood Cir", "Lockhart, TX", 47, 11400, "moderate", "Standard"],
    ["83 Laurel Crest", "New Braunfels, TX", 69, 23800, "strong", "Premium"],
    ["1611 Poplar St", "Schertz, TX", 39, 8200, "weak", "Standard"],
    ["94 Mulberry Pt", "Seguin, TX", 51, 14800, "moderate", "Premium"],
    ["722 Tulip Grove", "Boerne, TX", 24, 3800, "weak", "Standard"],
    ["12 Camellia Dr", "Helotes, TX", 66, 21500, "strong", "Premium"],
    ["888 Acacia Way", "Converse, TX", 42, 9100, "moderate", "Standard"],
    ["55 Persimmon Trce", "Universal City, TX", 73, 28700, "strong", "Premium"],
].map(([address, city, risk_score, exposureMid, leverage, report_type], i) => ({
    id: `p-plus-${i + 1}`,
    address,
    city,
    risk_score,
    risk_band: risk_score >= 70 ? "High" : risk_score >= 40 ? "Moderate" : "Low",
    repair_exposure_low: Math.round(exposureMid * 0.78),
    repair_exposure_high: Math.round(exposureMid * 1.22),
    high_priority_issues:
        risk_score >= 70 ? 4 : risk_score >= 55 ? 3 : risk_score >= 40 ? 2 : risk_score >= 25 ? 1 : 0,
    health_score: Math.max(38, 100 - risk_score - 4),
    report_type,
    analyzed_at: `${i + 11} days ago`,
    leverage,
    top_issue: "Premium analysis — open report for full breakdown",
}));

const PLUS_PROPERTIES = [...PRO_PROPERTIES, ...PLUS_EXTRA];

// Issue category aggregates per tier (used by Issue Categories card).
const ISSUE_CATEGORIES_LITE = [
    { label: "Roof & exterior", count: 3, exposure: 14200 },
    { label: "HVAC & mechanical", count: 4, exposure: 11800 },
    { label: "Foundation & structure", count: 2, exposure: 9700 },
    { label: "Plumbing", count: 2, exposure: 4400 },
    { label: "Electrical", count: 1, exposure: 2200 },
];

const ISSUE_CATEGORIES_PRO = [
    { label: "Roof & exterior", count: 12, exposure: 58400 },
    { label: "HVAC & mechanical", count: 14, exposure: 47200 },
    { label: "Foundation & structure", count: 9, exposure: 64100 },
    { label: "Plumbing", count: 11, exposure: 22800 },
    { label: "Electrical", count: 7, exposure: 19500 },
    { label: "Water intrusion", count: 5, exposure: 28700 },
    { label: "Drainage & grading", count: 6, exposure: 11400 },
];

const ISSUE_CATEGORIES_PLUS = [
    { label: "Roof & exterior", count: 31, exposure: 142600 },
    { label: "HVAC & mechanical", count: 36, exposure: 118400 },
    { label: "Foundation & structure", count: 22, exposure: 167900 },
    { label: "Plumbing", count: 28, exposure: 58200 },
    { label: "Electrical", count: 18, exposure: 49100 },
    { label: "Water intrusion", count: 14, exposure: 81700 },
    { label: "Drainage & grading", count: 17, exposure: 32900 },
    { label: "Insulation & energy", count: 12, exposure: 24600 },
];

// Risk distribution histograms (used by chart widget).
const RISK_DISTRIBUTION_PRO = [
    { band: "Low", count: 3, color: "#0f5c63" },
    { band: "Moderate", count: 5, color: "#D4A62A" },
    { band: "Elevated", count: 4, color: "#C85B3A" },
    { band: "High", count: 1, color: "#7E1F1A" },
];

const RISK_DISTRIBUTION_PLUS = [
    { band: "Low", count: 7, color: "#0f5c63" },
    { band: "Moderate", count: 11, color: "#D4A62A" },
    { band: "Elevated", count: 8, color: "#C85B3A" },
    { band: "High", count: 4, color: "#7E1F1A" },
];

const ACTIVITY_LITE = [
    { ts: "2h ago", label: "Generated Standard report — 123 Oak Street" },
    { ts: "Yesterday", label: "Re-opened 456 Maple Avenue" },
    { ts: "3d ago", label: "Downloaded PDF — 789 Cedar Lane" },
    { ts: "5d ago", label: "Shared report link with agent" },
];

const ACTIVITY_PRO = [
    { ts: "1h ago", label: "Premium Closing Strategy generated — 91 Aspen Crossing" },
    { ts: "3h ago", label: "Flagged 66 Sage Meadow as Negotiating" },
    { ts: "Yesterday", label: "Added contractor quote to 44 Willow Bend" },
    { ts: "2d ago", label: "Exported CSV — 8 properties" },
    { ts: "3d ago", label: "Compared 4 properties side-by-side" },
];

const ACTIVITY_PLUS = [
    { ts: "30m ago", label: "Bulk uploaded 6 inspection PDFs" },
    { ts: "2h ago", label: "Generated portfolio risk heatmap" },
    { ts: "5h ago", label: "Calibration: actual quote logged on 88 Holly Hill" },
    { ts: "Yesterday", label: "Exported full portfolio analytics" },
    { ts: "Yesterday", label: "Moved 3 properties to Closed pipeline" },
    { ts: "2d ago", label: "Long-term capital plan rebuilt for 2026" },
];

// Negotiation opportunities (Pro / Plus only).
const NEGOTIATION_OPPS_PRO = [
    { address: "44 Willow Bend", leverage: "Strong", potential_credit: "$18,000 – $24,000", insight: "Roof + chimney flashing + financing flags = layered concession path." },
    { address: "66 Sage Meadow", leverage: "Strong", potential_credit: "$26,000 – $34,000", insight: "Active water intrusion qualifies for repair-or-credit ultimatum." },
    { address: "201 Elm Court", leverage: "Strong", potential_credit: "$22,000 – $30,000", insight: "Electrical panel + foundation = sequenced concession strategy." },
];

const NEGOTIATION_OPPS_PLUS = [
    ...NEGOTIATION_OPPS_PRO,
    { address: "57 Juniper Way", leverage: "Strong", potential_credit: "$14,000 – $20,000", insight: "Bundle HVAC + grading into pre-closing repair credit." },
    { address: "55 Persimmon Trce", leverage: "Strong", potential_credit: "$16,000 – $22,000", insight: "Premium negotiation sequence: inspection period expiring fast." },
];

const HEATMAP_PLUS = (() => {
    // 6 regions × 4 categories grid, intensity 0–100.
    const regions = ["Austin Core", "Round Rock", "Cedar Park", "Pflugerville", "Buda/Kyle", "San Marcos"];
    const categories = ["Roof", "HVAC", "Foundation", "Water"];
    const rows = regions.map((r, ri) =>
        categories.map((c, ci) => {
            // deterministic pseudo-distribution
            const base = (ri * 17 + ci * 31) % 100;
            return Math.min(96, Math.max(8, base + (ci === 2 ? 14 : 0)));
        }),
    );
    return { regions, categories, rows };
})();

const RANKINGS_PLUS = PLUS_PROPERTIES
    .slice()
    .sort((a, b) => b.health_score - a.health_score)
    .slice(0, 6)
    .map((p, i) => ({
        rank: i + 1,
        address: p.address,
        health_score: p.health_score,
        risk_band: p.risk_band,
        exposure_high: p.repair_exposure_high,
    }));

const CAPITAL_PLAN_PLUS = [
    { window: "0 – 6 months", spend_low: 38000, spend_high: 56000, focus: "Water intrusion + foundation follow-ups" },
    { window: "6 – 18 months", spend_low: 72000, spend_high: 104000, focus: "Roof replacements + HVAC end-of-life" },
    { window: "18 – 36 months", spend_low: 41000, spend_high: 68000, focus: "Plumbing supply lines + electrical updates" },
    { window: "36 – 60 months", spend_low: 24000, spend_high: 39000, focus: "Cosmetic + energy efficiency upgrades" },
];

export const TIER_META = {
    lite: {
        id: "investor_lite",
        name: "Investor Lite",
        price: "$29",
        per: "/month",
        primary_cta_label: "Start Investor Lite",
        secondary_cta_label: "Compare Plans",
        tagline: "For occasional deal review",
        positioning: "Understand inspection reports faster",
        reports_remaining: "2 of 5 standard reports remaining this month",
        properties_analyzed: 3,
        report_type_default: "Standard",
        badge: null,
    },
    pro: {
        id: "investor_pro",
        name: "Investor Pro",
        price: "$79",
        per: "/month",
        primary_cta_label: "Start Investor Pro",
        secondary_cta_label: "Compare Plans",
        tagline: "Most Popular · Best for active investors",
        positioning: "Compare properties, negotiate smarter",
        reports_remaining: "3 of 10 premium reports remaining this month",
        properties_analyzed: 10,
        report_type_default: "Premium",
        badge: "Most Popular",
    },
    plus: {
        id: "investor_plus",
        name: "Investor Plus",
        price: "$149",
        per: "/month",
        primary_cta_label: "Start Investor Plus",
        secondary_cta_label: "Compare Plans",
        tagline: "Built for Serious Investors",
        positioning: "Operate inspection analysis at portfolio scale",
        reports_remaining: "12 of 25 premium reports remaining this month",
        properties_analyzed: 25,
        report_type_default: "Premium",
        badge: "Built for Serious Investors",
    },
};

export const TIER_DATA = {
    lite: {
        properties: LITE_WITH_LEVERAGE,
        issue_categories: ISSUE_CATEGORIES_LITE,
        risk_distribution: null,
        activity: ACTIVITY_LITE,
        negotiation_opps: null,
        heatmap: null,
        rankings: null,
        capital_plan: null,
    },
    pro: {
        properties: PRO_PROPERTIES,
        issue_categories: ISSUE_CATEGORIES_PRO,
        risk_distribution: RISK_DISTRIBUTION_PRO,
        activity: ACTIVITY_PRO,
        negotiation_opps: NEGOTIATION_OPPS_PRO,
        heatmap: null,
        rankings: null,
        capital_plan: null,
    },
    plus: {
        properties: PLUS_PROPERTIES,
        issue_categories: ISSUE_CATEGORIES_PLUS,
        risk_distribution: RISK_DISTRIBUTION_PLUS,
        activity: ACTIVITY_PLUS,
        negotiation_opps: NEGOTIATION_OPPS_PLUS,
        heatmap: HEATMAP_PLUS,
        rankings: RANKINGS_PLUS,
        capital_plan: CAPITAL_PLAN_PLUS,
    },
};

// Higher-tier "Locked" upgrade callouts shown inside lower-tier previews.
export const LOCKED_TEASERS = {
    lite: [
        {
            title: "Premium Negotiation Guidance",
            description: "Leverage scoring, seller concession sequencing, and contractor question prep on every report.",
            unlock_tier: "Investor Pro",
            unlock_plan_id: "investor_pro",
        },
        {
            title: "Deal Comparison Dashboard",
            description: "Compare repair exposure, leverage, and pipeline status across every property side-by-side.",
            unlock_tier: "Investor Pro",
            unlock_plan_id: "investor_pro",
        },
        {
            title: "Portfolio Analytics & Heatmaps",
            description: "Risk heatmaps, property rankings, and long-term capital planning across your portfolio.",
            unlock_tier: "Investor Plus",
            unlock_plan_id: "investor_plus",
        },
    ],
    pro: [
        {
            title: "Portfolio Risk Heatmap",
            description: "Visualize where structural, water, and HVAC concentration clusters across your regions.",
            unlock_tier: "Investor Plus",
            unlock_plan_id: "investor_plus",
        },
        {
            title: "Property Health Rankings",
            description: "Auto-ranked rollups of every property's health score, exposure ceiling, and risk band.",
            unlock_tier: "Investor Plus",
            unlock_plan_id: "investor_plus",
        },
        {
            title: "Long-Term Capital Planning",
            description: "12 / 36 / 60-month capital allocation outlook built from your stored inspection findings.",
            unlock_tier: "Investor Plus",
            unlock_plan_id: "investor_plus",
        },
        {
            title: "Bulk Upload Queue",
            description: "Drop multiple inspection PDFs at once and analyze them in parallel.",
            unlock_tier: "Investor Plus",
            unlock_plan_id: "investor_plus",
        },
    ],
    plus: [],
};

export function formatUsd(n) {
    if (n == null) return "—";
    if (n >= 1000) return `$${Math.round(n / 1000)}k`;
    return `$${n}`;
}

export function formatUsdRange(low, high) {
    if (low == null && high == null) return "—";
    return `${formatUsd(low)} – ${formatUsd(high)}`;
}

export function formatUsdFull(n) {
    if (n == null) return "—";
    return `$${n.toLocaleString()}`;
}
